import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { assert } from 'utils/assert'
import { createCategoriesTree } from 'features/categories/utils'
import { useCategoriesListQuery, useCategory } from 'features/categories/hooks'
import { SoftwareAdminApi, SoftwareApi, type SoftwareSchema, type VersionSchema } from 'openapi'
import { CONFIG } from 'config'
import { Skeleton } from 'shadcdn/ui/skeleton'
import { PromiseButton } from 'ui/Button/PromiseButton'
import { SoftwareRoutePath } from 'router'
import { AdminSoftwareListPath } from 'features/admin/routes'

import { AdminSoftwareVersionForm } from './components/AdminSoftwareVersionForm'
import { AdminSoftwareForm } from './components/AdminSoftwareForm'

type Props = {
  softwareId?: number
}

export const AdminSoftwarePage = (props: Props) => {
  const params = useParams()

  const softwareId = (props.softwareId ?? params.softwareId) ? Number(params.softwareId) : undefined
  assert(softwareId, 'No softwareId provided')

  const {
    data: dataSoftwareAdmin,
    isLoading: isLoadingSoftwareAdmin,
    refetch: refetchSoftware,
  } = useQuery({
    queryKey: ['SoftwareApi.get', softwareId],
    queryFn: () => SoftwareApi.get({ id: softwareId }),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const {
    data: versions,
    isLoading: isLoadingVersions,
    refetch: refetchVersion,
  } = useQuery<VersionSchema[]>({
    queryKey: ['SoftwareApi.getSofwareVersions', softwareId],
    queryFn: () => SoftwareApi.getSofwareVersions({ id: softwareId }),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const { data: categories, isLoading: isLoadingCategories } = useCategoriesListQuery()
  const categoriesWithChildren = useMemo(() => createCategoriesTree(categories || [], true), [categories])

  const [category, categoryLoading] = useCategory(dataSoftwareAdmin?.category_id || undefined)

  const isLoading = isLoadingSoftwareAdmin || isLoadingCategories || isLoadingVersions || categoryLoading

  if (isLoading)
    return (
      <div className="mx-5">
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-[700px] w-full mb-4" />
      </div>
    )

  assert(dataSoftwareAdmin, 'data is required')
  assert(versions, 'versions is required')
  return (
    <div className="mx-5">
      <div className="mb-2 flex">
        <div className="flex grow col gap-x-2 text-xs">
          <div className="font-bold">App</div>
          <Link to="/" className="link ">
            Home
          </Link>
          <Link
            to={SoftwareRoutePath({
              softwareId,
              categorySlug: category?.slug || '',
              vendorSlug: dataSoftwareAdmin.vendorSlug,
              softwareSlug: dataSoftwareAdmin.slug,
            })}
            className="link"
          >
            {dataSoftwareAdmin.name}
          </Link>
          <div className="ml-6 font-bold">Admin</div>
          <Link to={AdminSoftwareListPath()} className="link ">
            Software List
          </Link>
        </div>
        <AdminLinks dataSoftwareAdmin={dataSoftwareAdmin} />
      </div>
      <div className="w-full mb-24">
        <AdminSoftwareForm
          software={dataSoftwareAdmin}
          categories={categoriesWithChildren}
          onPostSave={refetchSoftware}
        />

        <div className="flex flex-wrap gap-x-4">
          {versions.map((version) => (
            <div className="w-full" key={version.id}>
              <AdminSoftwareVersionForm version={version} onPostSave={refetchVersion} />
            </div>
          ))}
          <div className="w-full mt-4 bg-white bg-opacity-90 rounded shadow text-center py-5">
            <PromiseButton
              size="lg"
              color="light"
              onClick={async () => {
                await SoftwareAdminApi.createVersion({ softwareId })
                refetchVersion()
              }}
            >
              Add version
            </PromiseButton>
          </div>
        </div>
      </div>
    </div>
  )
}

const AdminLinks = ({ dataSoftwareAdmin }: { dataSoftwareAdmin: SoftwareSchema }) => (
  <div className="text-xs flex gap-x-2">
    <span className="font-bold">Django Admin </span>{' '}
    <a
      className="link"
      href={`${CONFIG.API_BASE_URL}/admin/software/software/${dataSoftwareAdmin.id}/change/`}
      target="_blank"
      rel="noreferrer"
    >
      Software
    </a>{' '}
  </div>
)
